/** Widget configuration. Reads from window.__TERRA_SEARCH_CONFIG__ or <script> data attributes. */

export interface TerraSearchConfig {
  /** Base URL for Search Gateway (no trailing slash). */
  apiUrl: string;
  /** API key for Search Gateway authentication. */
  apiKey: string;
  /** Debounce delay in ms for autocomplete. Default: 200ms. */
  debounceMs: number;
  /** Number of autocomplete suggestions. Default: 8. */
  autocompleteLimit: number;
  /** Results per page. Default: 40. */
  perPage: number;
  /** Enable semantic search. Default: true. */
  semantic: boolean;
  /** Search results page URL pattern. Default: /catalogsearch/result/ */
  resultsPageUrl: string;
  /** Mount target selector. Default: #terra-search-root */
  mountSelector: string;
  /* ─── Display flags (controllable without redeploying widget) ─── */
  /** Show the lead-time badge on result cards when product is made-to-order. Default: false. */
  showLeadTime: boolean;
  /** Show the breadcrumb path above the product title. Default: true. */
  showBreadcrumb: boolean;
  /** Show the part number under the product title. Default: true. */
  showPartNumber: boolean;
  /** Show the In Stock badge on result cards. Default: true. */
  showInStockBadge: boolean;
  /** Show a CTA button on each sibling thumbnail in the variant carousel. Default: false. */
  showSiblingCtaButton: boolean;
  /** Show the In Stock badge on sibling thumbnails. Default: true. */
  showSiblingStockBadge: boolean;
  /** Show the description bullet list on result cards. Default: true. */
  showDescription: boolean;
}

declare global {
  interface Window {
    __TERRA_SEARCH_CONFIG__?: Partial<TerraSearchConfig>;
    dataLayer?: Record<string, unknown>[];
  }
}

const DEFAULTS: TerraSearchConfig = {
  apiUrl: '',
  apiKey: '',
  debounceMs: 200,
  autocompleteLimit: 8,
  perPage: 40,
  semantic: true,
  resultsPageUrl: '/catalogsearch/result/',
  mountSelector: '#terra-search-root',
  // Display flags — defaults reflect "what the customer should see today"
  // without needing a Forix redeploy. Toggle via window.__TERRA_SEARCH_CONFIG__
  // to flip a flag at runtime.
  showLeadTime: false,
  showBreadcrumb: true,
  showPartNumber: true,
  showInStockBadge: true,
  showSiblingCtaButton: false,
  showSiblingStockBadge: true,
  showDescription: true,
};

function readScriptDataAttrs(): Partial<TerraSearchConfig> {
  const script = document.querySelector('script[data-terra-search]');
  if (!script) return {};
  const cfg: Partial<TerraSearchConfig> = {};
  const apiUrl = script.getAttribute('data-api-url');
  if (apiUrl) cfg.apiUrl = apiUrl;
  const apiKey = script.getAttribute('data-api-key');
  if (apiKey) cfg.apiKey = apiKey;
  const mountSelector = script.getAttribute('data-mount');
  if (mountSelector) cfg.mountSelector = mountSelector;
  return cfg;
}

let _config: TerraSearchConfig | null = null;

export function getConfig(): TerraSearchConfig {
  if (_config) return _config;
  const windowCfg = window.__TERRA_SEARCH_CONFIG__ || {};
  const dataCfg = readScriptDataAttrs();
  _config = { ...DEFAULTS, ...windowCfg, ...dataCfg };
  // Strip trailing slash from apiUrl
  _config.apiUrl = _config.apiUrl.replace(/\/+$/, '');
  if (!_config.apiUrl) {
    console.error('[TerraSearch] FATAL: apiUrl is not configured. Set window.__TERRA_SEARCH_CONFIG__.apiUrl or use data-api-url attribute.');
  }
  // apiKey is optional — when empty, authentication is handled server-side
  // by the reverse proxy injecting the X-API-Key header.
  return _config;
}
